import { Link, createFileRoute } from '@tanstack/react-router';
import { MailCheck } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Button, Field, Input } from '@/components/ui';
import { AuthLayout } from '@/features/auth/auth-layout';
import { authClient, unwrap } from '@/lib/auth';

export const Route = createFileRoute('/forgot-password')({ component: ForgotPasswordPage });

function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setPending(true);
    try {
      unwrap(
        await authClient.requestPasswordReset({
          email,
          redirectTo: `${window.location.origin}/reset-password`,
        }),
      );
      setSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setPending(false);
    }
  };

  return (
    <AuthLayout
      title="Reset your password"
      subtitle="We will email you a link to choose a new one."
      footer={
        <Link to="/login" className="font-medium text-accent-text hover:underline">
          Back to sign in
        </Link>
      }
    >
      {sent ? (
        <div className="flex flex-col items-center py-2 text-center" role="status">
          <MailCheck className="mb-3 size-8 text-success" />
          <p className="font-medium">Check your email</p>
          <p className="mt-1 text-sm text-fg-muted">
            If an account exists for {email}, a reset link is on its way.
          </p>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-3.5" noValidate>
          <Field label="Email" htmlFor="email" error={error}>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              required
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="h-9"
            />
          </Field>
          <Button type="submit" variant="primary" size="lg" className="w-full" loading={pending}>
            Send reset link
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}
